import React, { useEffect, useMemo, useRef, useState } from 'react';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

function wsApi(path) {
  const base = API_BASE || (typeof window !== 'undefined' ? window.location.origin : '');
  const url = new URL(base + (path.startsWith('/') ? path : `/${path}`), typeof window !== 'undefined' ? window.location.href : 'http://localhost/');
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

const DEFAULT_MARKET_URL = 'https://predict.fun/zh-cn/market/what-price-will-cashcat-hit-before-sept-2026';
const STORAGE_KEY = 'predict_primary_arb_market';
const DEFAULT_DEX_URL = 'https://dexscreener.com/robinhood/0xa70fc67c9f69da90b63a0e4c05d229954574e313';
const STORAGE_MONITORS_KEY = 'predict_primary_arb_monitors_v1';
const DEFAULT_HYPE_SYMBOL = 'CASHCAT';

function createMonitor(overrides = {}) {
  const marketUrl = String(overrides.marketUrl || overrides.url || DEFAULT_MARKET_URL).trim();
  const slug = parseSlug(marketUrl);
  return {
    id: String(overrides.id || `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`),
    name: String(overrides.name || '').trim(),
    marketUrl,
    slug,
    dexUrl: String(overrides.dexUrl || overrides.dex || DEFAULT_DEX_URL).trim(),
    hypeSymbol: String(overrides.hypeSymbol ?? overrides.hype ?? DEFAULT_HYPE_SYMBOL).trim().toUpperCase(),
    enabled: overrides.enabled !== false,
    createdAt: Number(overrides.createdAt || Date.now()),
  };
}

function loadStoredMonitors() {
  try {
    const raw = localStorage.getItem(STORAGE_MONITORS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map((item) => createMonitor(item));
    }
    const legacy = localStorage.getItem(STORAGE_KEY);
    return [createMonitor({ marketUrl: legacy || DEFAULT_MARKET_URL })];
  } catch {
    return [createMonitor()];
  }
}

function saveStoredMonitors(monitors) {
  try {
    localStorage.setItem(STORAGE_MONITORS_KEY, JSON.stringify(monitors));
  } catch (_) {}
}

function parseSlug(input) {
  const text = String(input || '').trim();
  const match = text.match(/\/market\/([A-Za-z0-9_-]+)/i);
  return (match ? match[1] : text).replace(/[^\w-]/g, '') || 'what-price-will-cashcat-hit-before-sept-2026';
}

function numberValue(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatUsd(value, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: digits })}`;
}

function formatCompactUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2).replace(/\.00$/, '')}B`;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.00$/, '')}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return formatUsd(n, 2);
}

function formatPrice(value, digits = 4) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `$${n.toFixed(digits)}`;
}

function formatRate(value, digits = 4) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n >= 0 ? '+' : ''}${(n * 100).toFixed(digits)}%`;
}

function formatCents(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${(n * 100).toFixed(1)}¢`;
}

function formatPercent(value, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${(n * 100).toFixed(digits)}%`;
}

function allInNoPrice(noPrice, feeRate) {
  const no = Number(noPrice);
  const rate = Number(feeRate);
  if (!Number.isFinite(no) || no <= 0 || no >= 1 || !Number.isFinite(rate)) return 0;
  return no + rate * Math.min(no, 1 - no);
}

function solveNoTriggerPrice(maxAllInCost, feeRate) {
  const target = Number(maxAllInCost);
  if (!Number.isFinite(target) || target <= 0) return 0;
  let low = 0;
  let high = 0.999;
  for (let i = 0; i < 36; i += 1) {
    const mid = (low + high) / 2;
    if (allInNoPrice(mid, feeRate) <= target) low = mid;
    else high = mid;
  }
  return Math.max(0, Math.min(0.999, low));
}

function calculateMakerNoPlan(targetFdv, settings, makerPriceCents, makerShares) {
  const target = Number(targetFdv);
  const totalSupply = Number(settings.totalSupply);
  const spotBuyFee = Number(settings.spotBuyFee);
  const spotSellFee = Number(settings.spotSellFee);
  const currentSpotPrice = Number(settings.currentSpotPrice);
  const price = Number(makerPriceCents) / 100;
  const shares = Number(makerShares);
  const spotBudgetUsd = 100;
  if (
    !Number.isFinite(target) || target <= 0
    || !Number.isFinite(totalSupply) || totalSupply <= 0
    || !Number.isFinite(price) || price <= 0 || price >= 1
    || !Number.isFinite(shares) || shares <= 0
  ) return null;
  const targetSpotPrice = target / totalSupply;
  const noCost = price * shares;
  const noProfit = shares - noCost;
  const equivalentSpotPrice = price > 0
    ? (targetSpotPrice * (1 - spotSellFee)) / ((1 + spotBuyFee) * (1 + price))
    : targetSpotPrice;
  const spotProfitPerUsd = Number.isFinite(currentSpotPrice) && currentSpotPrice > 0
    ? (targetSpotPrice * (1 - spotSellFee)) / ((1 + spotBuyFee) * currentSpotPrice) - 1
    : 0;
  const spotProfitPer100 = spotBudgetUsd * spotProfitPerUsd;
  const requiredSpotBudget = spotProfitPerUsd > 0 ? noCost / spotProfitPerUsd : Infinity;
  const drawdownGuardRate = 0.5;
  const maxSpotBudgetByGuard = noProfit > 0 ? noProfit / drawdownGuardRate : 0;
  const guardGap = Number.isFinite(requiredSpotBudget) ? maxSpotBudgetByGuard - requiredSpotBudget : -Infinity;
  const requiredSpotTokens = Number.isFinite(requiredSpotBudget) && currentSpotPrice > 0
    ? requiredSpotBudget / ((1 + spotBuyFee) * currentSpotPrice)
    : 0;
  const requiredSpotProfit = Number.isFinite(requiredSpotBudget) ? requiredSpotBudget * spotProfitPerUsd : 0;
  const maxGuardSpotProfit = maxSpotBudgetByGuard * spotProfitPerUsd;
  const requiredSpotMaxDrawdownPct = Number.isFinite(requiredSpotBudget) && requiredSpotBudget > 0
    ? noProfit / requiredSpotBudget
    : 0;
  const isCostCovered = spotProfitPerUsd > 0 && Number.isFinite(requiredSpotBudget);
  const isGuardCovered = isCostCovered && requiredSpotBudget <= maxSpotBudgetByGuard;
  return {
    price,
    shares,
    noCost,
    noProfit,
    spotBudgetUsd,
    equivalentSpotPrice,
    spotProfitPerUsd,
    spotProfitPer100,
    requiredSpotBudget,
    requiredSpotTokens,
    requiredSpotProfit,
    requiredSpotMaxDrawdownPct,
    drawdownGuardRate,
    maxSpotBudgetByGuard,
    maxGuardSpotProfit,
    guardGap,
    targetSpotPrice,
    isCostCovered,
    isGuardCovered,
    isPositiveNow: isCostCovered && isGuardCovered,
  };
}

function formatShares(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
}

function formatDateTime(value) {
  if (!value) return '-';
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return '-';
  return new Date(ts).toLocaleString('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function calculateThreshold(targetFdv, noPrice, settings) {
  const target = Number(targetFdv);
  const no = Number(noPrice);
  const totalSupply = Number(settings.totalSupply);
  const spotBuyFee = Number(settings.spotBuyFee);
  const spotSellFee = Number(settings.spotSellFee);
  const predictTakerFeeRate = Number(settings.predictTakerFeeRate);
  const currentFdv = Number(settings.currentFdv);
  if (!Number.isFinite(target) || target <= 0 || !Number.isFinite(no) || no <= 0 || no >= 1 || !Number.isFinite(totalSupply) || totalSupply <= 0) return null;
  const predictFeePerShare = predictTakerFeeRate * Math.min(no, 1 - no);
  const allInNoCost = allInNoPrice(no, predictTakerFeeRate);
  const targetSpotPrice = target / totalSupply;
  const currentSpotPrice = Number(settings.currentSpotPrice);
  const planBudgetUsd = 100;
  const planNoShares = 100;
  const noCostForPlan = allInNoCost * planNoShares;
  const spotTokensForPlan = Number.isFinite(currentSpotPrice) && currentSpotPrice > 0
    ? planBudgetUsd / ((1 + spotBuyFee) * currentSpotPrice)
    : 0;
  const spotProceedsAtTarget = spotTokensForPlan * targetSpotPrice * (1 - spotSellFee);
  const spotProfitAtTarget = spotProceedsAtTarget - planBudgetUsd;
  const noProfitForPlan = planNoShares - noCostForPlan;
  const drawdownGuardUsd = planBudgetUsd * 0.5;
  const guardEdgeUsd = noProfitForPlan - drawdownGuardUsd;
  const edgeUsd = spotProfitAtTarget - noCostForPlan;
  const maxNoCostBySpotProfit = Math.max(0, spotProfitAtTarget) / planNoShares;
  const maxNoCostByDrawdownGuard = Math.max(0, planNoShares - drawdownGuardUsd) / planNoShares;
  const triggerNoPrice = solveNoTriggerPrice(Math.min(maxNoCostBySpotProfit, maxNoCostByDrawdownGuard), predictTakerFeeRate);
  const spotTriggerPrice = noCostForPlan > 0
    ? (planBudgetUsd * targetSpotPrice * (1 - spotSellFee)) / ((planBudgetUsd + noCostForPlan) * (1 + spotBuyFee))
    : targetSpotPrice;
  const thresholdSpotPrice = spotTriggerPrice;
  const thresholdFdv = thresholdSpotPrice * totalSupply;
  const gapPct = Number.isFinite(currentSpotPrice) && currentSpotPrice > 0 ? Math.max(0, (currentSpotPrice - thresholdSpotPrice) / currentSpotPrice) : 0;
  let status = 'INACTIVE';
  if (edgeUsd >= 0 && guardEdgeUsd >= 0) status = 'ACTIVE';
  else if (
    (edgeUsd >= -5 && guardEdgeUsd >= -5)
    || (Number.isFinite(currentSpotPrice) && currentSpotPrice > 0 && currentSpotPrice <= thresholdSpotPrice * 1.03 && guardEdgeUsd >= -5)
  ) status = 'NEAR';
  return {
    targetFdv: target,
    currentFdv,
    currentSpotPrice,
    totalSupply,
    spotBuyFee,
    spotSellFee,
    predictFeePerShare,
    allInNoCost,
    thresholdFdv,
    thresholdSpotPrice,
    targetSpotPrice,
    planBudgetUsd,
    planNoShares,
    planNoCost: noCostForPlan,
    noCostForPlan,
    spotTokensForPlan,
    spotProceedsAtTarget,
    spotProfitAtTarget,
    noProfitForPlan,
    drawdownGuardUsd,
    guardEdgeUsd,
    edgeUsd,
    triggerNoPrice,
    spotTriggerPrice,
    gapPct,
    status,
  };
}

function statusText(status) {
  if (status === 'ACTIVE') return '已进入套利区';
  if (status === 'NEAR') return '接近阈值';
  return '等待现货回落';
}

function nextAction(outcome, threshold) {
  if (!outcome?.noPrice) return '等待 NO 盘口出现后再判断。';
  if (!threshold) return '缺少目标 FDV 或价格，暂不判断。';
  if (threshold.status === 'ACTIVE') {
    return '现货利润覆盖 NO 成本，且 NO 赢利覆盖 50% 回撤。';
  }
  if (threshold.status === 'NEAR') {
    const costGap = Math.max(0, -threshold.edgeUsd);
    const guardGap = Math.max(0, -threshold.guardEdgeUsd);
    return `接近套利区：成本差 ${formatUsd(costGap, 1)}，兜底差 ${formatUsd(guardGap, 1)}。`;
  }
  const costGap = Math.max(0, -threshold.edgeUsd);
  const guardGap = Math.max(0, -threshold.guardEdgeUsd);
  return `未达标：成本差 ${formatUsd(costGap, 1)}，兜底差 ${formatUsd(guardGap, 1)}。`;
}

function Chart({ outcomes }) {
  const palette = ['#6d5dfc', '#ff9f43', '#ff5f95', '#3478ff', '#13b981'];
  const seriesList = (outcomes || []).slice(0, 5).map((item, index) => ({
    ...item,
    color: palette[index % palette.length],
    series: Array.isArray(item.series) ? item.series : [],
  })).filter((item) => item.series.length >= 2);
  const domain = useMemo(() => {
    const points = seriesList.flatMap((item) => item.series);
    const xs = points.map((point) => Number(point.x)).filter(Number.isFinite);
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
    };
  }, [seriesList]);
  if (!seriesList.length || !Number.isFinite(domain.minX) || !Number.isFinite(domain.maxX) || domain.minX === domain.maxX) {
    return <div className="primary-arb-chart-empty">暂无走势图数据</div>;
  }
  const width = 1000;
  const height = 330;
  const pad = { left: 34, right: 120, top: 18, bottom: 34 };
  const xScale = (x) => pad.left + ((Number(x) - domain.minX) / (domain.maxX - domain.minX)) * (width - pad.left - pad.right);
  const yScale = (y) => pad.top + (1 - Math.max(0, Math.min(1, Number(y)))) * (height - pad.top - pad.bottom);
  const labelX = width - pad.right + 18;
  return (
    <div className="primary-arb-chart-card">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="概率走势图">
        {[0, 0.2, 0.4, 0.6, 0.8, 1].map((y) => (
          <g key={y}>
            <line x1={pad.left} x2={width - pad.right} y1={yScale(y)} y2={yScale(y)} />
            <text x={width - 34} y={yScale(y) + 4}>{Math.round(y * 100)}%</text>
          </g>
        ))}
        {seriesList.map((item) => {
          const points = item.series.map((point) => `${xScale(point.x)},${yScale(point.y)}`).join(' ');
          const last = item.series[item.series.length - 1];
          return (
            <g key={item.id}>
              <polyline points={points} stroke={item.color} />
              <circle cx={xScale(last.x)} cy={yScale(last.y)} r="5" fill={item.color} />
              <text className="primary-arb-chart-label" x={labelX} y={yScale(last.y) - 6} fill={item.color}>
                {item.label}
              </text>
              <text className="primary-arb-chart-percent" x={labelX} y={yScale(last.y) + 24} fill={item.color}>
                {formatPercent(last.y)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="primary-arb-range-tabs">
        {['1h', '3h', '24h', '7d', '30d', '全部'].map((item) => <span key={item} className={item === '7d' ? 'active' : ''}>{item}</span>)}
      </div>
    </div>
  );
}

const CXMT_MARKET_SLUG = 'cxmt-ipo-closing-market-cap';
const CXMT_MARKET_URL = 'https://predict.fun/zh-cn/market/cxmt-ipo-closing-market-cap';
const CXMT_HYPE_SYMBOL = 'xyz:CXMT';
const CXMT_DEFAULT_USD_CNY = 6.7685;
const CXMT_DEFAULT_TOTAL_SHARES_YI = 668.8;

function formatCny(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function formatRmbYi(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(2)} 万亿元`;
  return `${n.toFixed(0)} 亿元`;
}

function formatCxmtCents(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${(n * 100).toFixed(1)}¢`;
}

function parseCxmtMarketCapBracket(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  if (/不进行\s*IPO|No IPO/i.test(raw)) {
    return { type: 'noIpo', minYi: null, maxYi: null, label: '不 IPO' };
  }
  let matches = [...raw.matchAll(/(\d+(?:\.\d+)?)\s*万亿元?/g)].map((item) => Number(item[1]) * 10000);
  if (!matches.length) {
    matches = [...raw.matchAll(/[¥￥]?\s*(\d+(?:\.\d+)?)\s*T\b/gi)].map((item) => Number(item[1]) * 10000);
  }
  if (!matches.length) return null;
  const lowerRaw = raw.toLowerCase();
  if (/^[<＜]|低于|小于|少于|less than|below|under/.test(lowerRaw)) {
    return { type: 'range', minYi: 0, maxYi: matches[0], label: `< ${formatRmbYi(matches[0])}` };
  }
  if (/[≥>=＞]|以上|不低于|高于|greater than|more than|at least|or more/.test(lowerRaw)) {
    return { type: 'range', minYi: matches[0], maxYi: Infinity, label: `≥ ${formatRmbYi(matches[0])}` };
  }
  if (matches.length >= 2) {
    return {
      type: 'range',
      minYi: Math.min(matches[0], matches[1]),
      maxYi: Math.max(matches[0], matches[1]),
      label: `${formatRmbYi(Math.min(matches[0], matches[1]))} - ${formatRmbYi(Math.max(matches[0], matches[1]))}`,
    };
  }
  return { type: 'range', minYi: matches[0], maxYi: matches[0], label: formatRmbYi(matches[0]) };
}

function getCxmtBracketState(bracket, impliedYi) {
  if (!bracket || bracket.type === 'noIpo') {
    return { tone: 'muted', title: '不参与估值比较', detail: 'HYPE 有盘前价格时，通常说明市场在定价 IPO 发生后的估值。' };
  }
  const value = Number(impliedYi);
  if (!Number.isFinite(value)) {
    return { tone: 'muted', title: '等待 HYPE 价格', detail: '拿到 HYPE CXMT 实时价格后会自动换算。' };
  }
  const minYi = Number(bracket.minYi || 0);
  const maxYi = Number(bracket.maxYi);
  if (value >= minYi && (!Number.isFinite(maxYi) || value < maxYi)) {
    return { tone: 'active', title: '当前 HYPE 落在该区间', detail: `隐含市值 ${formatRmbYi(value)}，与该 Predict 档位同频。` };
  }
  if (value < minYi) {
    const diff = minYi - value;
    const pct = value > 0 ? diff / value : 0;
    return { tone: 'below', title: 'HYPE 低于该区间', detail: `还需上涨 ${formatRmbYi(diff)}，约 ${(pct * 100).toFixed(2)}%。` };
  }
  const diff = value - maxYi;
  const pct = maxYi > 0 ? diff / maxYi : 0;
  return { tone: 'above', title: 'HYPE 高于该区间', detail: `已高出 ${formatRmbYi(diff)}，约 ${(pct * 100).toFixed(2)}%。` };
}

function CxmtSpreadPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [usdCny, setUsdCny] = useState(String(CXMT_DEFAULT_USD_CNY));
  const [totalSharesYi, setTotalSharesYi] = useState(String(CXMT_DEFAULT_TOTAL_SHARES_YI));

  async function loadCxmt(silent = false) {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({
        slug: CXMT_MARKET_SLUG,
        hype: CXMT_HYPE_SYMBOL,
      });
      const resp = await fetch(api(`/api/primary-arbitrage?${params.toString()}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `加载失败 ${resp.status}`);
      setData(body.data || null);
      setMessage('');
    } catch (err) {
      setMessage(err?.message || '长鑫价差数据读取失败');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadCxmt();
    const timer = window.setInterval(() => loadCxmt(true), 3000);
    return () => window.clearInterval(timer);
  }, []);

  const fx = Number(usdCny);
  const sharesYi = Number(totalSharesYi);
  const hypePrice = Number(data?.hype?.price);
  const hypeFunding = Number(data?.hype?.funding);
  const rmbSharePrice = Number.isFinite(hypePrice) && Number.isFinite(fx) ? hypePrice * fx : null;
  const impliedMarketCapYi = Number.isFinite(rmbSharePrice) && Number.isFinite(sharesYi) ? rmbSharePrice * sharesYi : null;
  const updatedAt = data?.syncedAt ? new Date(data.syncedAt).toLocaleTimeString('zh-CN', { hour12: false }) : '-';
  const rows = (Array.isArray(data?.outcomes) ? data.outcomes : [])
    .map((item) => {
      const title = item.label || item.title || item.question || '未命名档位';
      const bracket = parseCxmtMarketCapBracket(title);
      const state = getCxmtBracketState(bracket, impliedMarketCapYi);
      return { ...item, title, bracket, state };
    })
    .sort((a, b) => {
      if (a.bracket?.type === 'noIpo') return 1;
      if (b.bracket?.type === 'noIpo') return -1;
      return Number(a.bracket?.minYi || 0) - Number(b.bracket?.minYi || 0);
    });

  const activeRow = rows.find((item) => item.state?.tone === 'active');

  return (
    <div className="cxmt-spread-page">
      <section className="cxmt-hero">
        <div>
          <span className="cxmt-eyebrow">CXMT Valuation Monitor</span>
          <h1>长鑫价差套利</h1>
          <p>同步读取 Predict 长鑫 IPO 市值区间和 Hyperliquid xyz:CXMT 盘前价格，将 HYPE 的美元单股价格换算成人民币市值，用来判断 Predict 档位是否低估或高估。</p>
        </div>
        <div className="cxmt-hero-actions">
          <a href={CXMT_MARKET_URL} target="_blank" rel="noreferrer">打开 Predict 市场</a>
          <a href="https://app.hyperliquid.xyz/trade/xyz:CXMT" target="_blank" rel="noreferrer">打开 HYPE CXMT</a>
        </div>
      </section>

      <section className="cxmt-formula-card">
        <div>
          <b>换算公式</b>
          <p>人民币市值（亿元） = HYPE CXMT 价格 × 美元兑人民币 × 668.8。这里的 668.8 是约 668.8 亿股总股本，不是本次发行的 66.88 亿股。</p>
        </div>
        <div>
          <b>Predict 规则</b>
          <p>Predict 最终按首个交易日官方收盘价 × 官方总发行在外股份结算。HYPE 只作为盘前预期参考，不是 Predict 的结算来源。</p>
        </div>
        <div>
          <b>当前同频区间</b>
          <p>{activeRow ? `HYPE 当前隐含 ${formatRmbYi(impliedMarketCapYi)}，对应 Predict 档位：${activeRow.title}` : '等待 HYPE 价格或暂无命中区间。'}</p>
        </div>
      </section>

      <section className="cxmt-config">
        <label>
          美元兑人民币
          <input value={usdCny} onChange={(event) => setUsdCny(event.target.value)} />
        </label>
        <label>
          总股本（亿股）
          <input value={totalSharesYi} onChange={(event) => setTotalSharesYi(event.target.value)} />
        </label>
        <button type="button" onClick={() => loadCxmt()} disabled={loading}>{loading ? '刷新中' : '立即刷新'}</button>
        <div>
          <span>同步频率</span>
          <strong>3 秒</strong>
          <small>最后更新 {updatedAt}</small>
        </div>
      </section>

      {message && <div className="primary-arb-message">{message}</div>}

      <section className="cxmt-metrics">
        <div>
          <span>HYPE CXMT</span>
          <strong>{formatPrice(hypePrice, 3)}</strong>
          <small>{data?.hype?.source === 'hyperliquid' ? 'Hyperliquid xyz 实时' : (data?.hype?.error || '等待 HYPE 数据')}</small>
        </div>
        <div>
          <span>折算人民币股价</span>
          <strong>{formatCny(rmbSharePrice, 2)}</strong>
          <small>HYPE 价格 × USD/CNY</small>
        </div>
        <div>
          <span>隐含人民币市值</span>
          <strong>{formatRmbYi(impliedMarketCapYi)}</strong>
          <small>用于对比 Predict 各档市值</small>
        </div>
        <div>
          <span>HYPE 资金费率</span>
          <strong>{Number.isFinite(hypeFunding) ? formatRate(hypeFunding, 4) : '-'}</strong>
          <small>负费率表示多头收取，正费率表示多头支付</small>
        </div>
      </section>

      <section className="cxmt-results">
        <div className="cxmt-results-head">
          <div>
            <h2>{rows.length || 0} 个 Predict 档位</h2>
            <p>蓝色是当前 HYPE 隐含市值命中的档位，黄色/红色提示该档位相对 HYPE 的方向偏离。</p>
          </div>
          <span>HYPE 隐含：{formatRmbYi(impliedMarketCapYi)}</span>
        </div>
        <div className="cxmt-table">
          {rows.map((item) => (
            <article key={item.id || item.title} className={`cxmt-row ${item.state?.tone || 'muted'}`}>
              <div className="cxmt-market-title">
                {item.imageUrl && <img src={item.imageUrl} alt="" />}
                <div>
                  <a href={item.url || CXMT_MARKET_URL} target="_blank" rel="noreferrer">{item.title}</a>
                  <span>{item.bracket?.label || '未识别区间'}</span>
                </div>
              </div>
              <div className="cxmt-prob">
                <strong>{formatPercent(item.probability ?? item.yesPrice, 0)}</strong>
                <span>概率</span>
              </div>
              <div className="cxmt-prices">
                <button type="button">是 {formatCxmtCents(item.yesPrice)}</button>
                <button type="button" className="no">否 {formatCxmtCents(item.noPrice)}</button>
              </div>
              <div className="cxmt-valuation">
                <b>{item.state?.title}</b>
                <p>{item.state?.detail}</p>
              </div>
              <div className="cxmt-volume">
                <span>成交量</span>
                <strong>{formatCompactUsd(item.volumeUsd)}</strong>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

const UNITREE_HYPE_SYMBOL = 'xyz:UNITREE';
const UNITREE_HYPE_URL = 'https://app.hyperliquid.xyz/trade/xyz:UNITREE';
const UNITREE_DOCS_URL = 'https://docs.trade.xyz/consolidated-resources/specification-index-ipops';
/** 离岸 USD/CNH 近似；可在页面手动改 */
const UNITREE_DEFAULT_USD_CNY = 6.7425;
/** 发行后总股本 40,446.4340 万股 = 4.04464340 亿股 */
const UNITREE_DEFAULT_TOTAL_SHARES_YI = 4.0446434;
/** 2026-08-06 公告确定的科创板发行价（元/股） */
const UNITREE_IPO_PRICE_CNY = 150.8;
const UNITREE_IPO_CODE = '688836';
const UNITREE_IPO_SUBSCRIBE_DATE = '2026-08-10';

/** 长鑫 × 海力士市值汇率监控默认参数 */
const CXMT_HYNIX_MCAP_STORAGE_KEY = 'predict_cxmt_hynix_mcap_v1';
const HYNIX_DEFAULT_TOTAL_SHARES_YI = 7.2887;
const CXMT_HYNIX_MCAP_DEFAULT_BASELINE = '';

function loadCxmtHynixMcapSettings() {
  try {
    const raw = localStorage.getItem(CXMT_HYNIX_MCAP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function formatUsdYi(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(2)} 万亿美元`;
  return `${n.toFixed(2)} 亿美元`;
}

function formatFxRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toFixed(4);
}

function CxmtHynixMcapPanel() {
  const stored = useMemo(() => loadCxmtHynixMcapSettings(), []);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [baselineRatio, setBaselineRatio] = useState(String(stored?.baselineRatio ?? CXMT_HYNIX_MCAP_DEFAULT_BASELINE));
  const baselineLockedRef = useRef(Boolean(stored?.baselineRatio));

  useEffect(() => {
    try {
      localStorage.setItem(CXMT_HYNIX_MCAP_STORAGE_KEY, JSON.stringify({ baselineRatio }));
    } catch (_) {}
  }, [baselineRatio]);

  async function loadMcap(silent = false) {
    if (!silent) setLoading(true);
    try {
      const resp = await fetch(api('/api/cxmt-hynix-mcap'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `加载失败 ${resp.status}`);
      setData(body.data || null);
      setMessage('');
    } catch (err) {
      setMessage(err?.message || '官方市值读取失败');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadMcap();
    const timer = window.setInterval(() => loadMcap(true), 5000);
    return () => window.clearInterval(timer);
  }, []);

  const usdCny = Number(data?.fx?.usdCny);
  const usdKrw = Number(data?.fx?.usdKrw);
  const cxmtPrice = Number(data?.cxmt?.price);
  const hynixPrice = Number(data?.hynix?.price);
  const cxmtMcapUsdYi = Number(data?.cxmt?.mcapUsdYi);
  const cxmtMcapCnyYi = Number(data?.cxmt?.mcapCnyYi);
  const hynixMcapUsdYi = Number(data?.hynix?.mcapUsdYi);
  const hynixMcapCnyYi = Number(data?.hynix?.mcapCnyYi);
  const hynixMcapKrwYi = Number(data?.hynix?.mcapKrwYi);
  const fxRatio = Number(data?.fxRatio);
  const hypeImpliedUsdYi = Number(data?.reference?.hypeImpliedUsdYi);
  const hypeCxmtPrice = Number(data?.reference?.hypeCxmt?.price);
  const binancePrice = Number(data?.reference?.binance?.price);
  const baseline = Number(baselineRatio);
  const deviationPct = Number.isFinite(fxRatio) && Number.isFinite(baseline) && baseline > 0
    ? (fxRatio - baseline) / baseline
    : null;
  const positive = Number.isFinite(deviationPct) && deviationPct > 0;
  const negative = Number.isFinite(deviationPct) && deviationPct < 0;
  const statusText = !Number.isFinite(fxRatio)
    ? '等待官方市值'
    : !Number.isFinite(baseline)
      ? '已算汇率，设基线后可看偏离'
      : positive
        ? '长鑫相对偏贵'
        : negative
          ? '长鑫相对偏便宜'
          : '贴近基线';
  const lastText = data?.syncedAt ? new Date(data.syncedAt).toLocaleTimeString('zh-CN', { hour12: false }) : '-';
  const cxmtSharesYi = Number(data?.cxmt?.sharesYi);
  const hynixSharesYi = Number(data?.hynix?.sharesYi);

  useEffect(() => {
    if (!Number.isFinite(fxRatio) || fxRatio <= 0) return;
    setHistory((prev) => {
      const last = prev[prev.length - 1];
      if (last && Math.abs(Number(last.fxRatio) - fxRatio) < 1e-8 && Date.now() - Number(last.ts) < 2000) return prev;
      return [...prev.slice(-79), { ts: Date.now(), fxRatio }];
    });
    if (!baselineLockedRef.current && !String(baselineRatio || '').trim()) {
      baselineLockedRef.current = true;
      setBaselineRatio(fxRatio.toFixed(4));
    }
  }, [fxRatio, baselineRatio]);

  const chartPoints = history.length >= 2 ? history : [
    { ts: Date.now() - 60_000, fxRatio: Number.isFinite(fxRatio) ? fxRatio : 0 },
    { ts: Date.now(), fxRatio: Number.isFinite(fxRatio) ? fxRatio : 0 },
  ];
  const min = Math.min(...chartPoints.map((item) => item.fxRatio), Number.isFinite(baseline) ? baseline : Infinity);
  const max = Math.max(...chartPoints.map((item) => item.fxRatio), Number.isFinite(baseline) ? baseline : -Infinity);
  const safeMin = Number.isFinite(min) ? min : 0;
  const safeMax = Number.isFinite(max) ? max : 1;
  const range = safeMax - safeMin || 0.01;
  const sparkPoints = chartPoints.map((item, index) => {
    const x = chartPoints.length <= 1 ? 0 : (index / (chartPoints.length - 1)) * 100;
    const y = 84 - ((item.fxRatio - safeMin) / range) * 68;
    return `${x},${y}`;
  }).join(' ');
  const baselineY = Number.isFinite(baseline)
    ? 84 - ((baseline - safeMin) / range) * 68
    : null;

  return (
    <div className="cxmt-hynix-mcap-page">
      <section className="cxmt-hynix-hero">
        <div>
          <span className="cxmt-hynix-eyebrow">Official Market Cap FX</span>
          <h1>长鑫 × 海力士市值汇率</h1>
          <p>
            直接读取官方股票市值：科创板 <b>688825 长鑫科技</b> 与韩股 <b>000660 SK海力士</b>，
            按 Yahoo 汇率折成美元后对比「长鑫市值 / 海力士市值」。
          </p>
        </div>
        <div className="cxmt-hynix-hero-actions">
          <a href={data?.cxmt?.url || 'https://quote.eastmoney.com/sh688825.html'} target="_blank" rel="noreferrer">长鑫 688825</a>
          <a href={data?.hynix?.url || 'https://finance.yahoo.com/quote/000660.KS/'} target="_blank" rel="noreferrer">海力士 000660</a>
        </div>
      </section>

      <section className="cxmt-hynix-formula">
        <div>
          <b>长鑫（688825）</b>
          <p>东方财富总市值（人民币）÷ USD/CNY = 美元市值。股本约 {Number.isFinite(cxmtSharesYi) ? cxmtSharesYi.toFixed(2) : '668.8'} 亿股。</p>
        </div>
        <div>
          <b>海力士（000660）</b>
          <p>东方财富总市值（韩元）÷ USD/KRW = 美元市值。股本约 {Number.isFinite(hynixSharesYi) ? hynixSharesYi.toFixed(4) : '7.2887'} 亿股。</p>
        </div>
        <div>
          <b>市值汇率</b>
          <p>汇率 = 长鑫美元市值 ÷ 海力士美元市值。相对基线偏离 &gt; 0 表示长鑫相对更贵。</p>
        </div>
      </section>

      <section className="cxmt-hynix-config cxmt-hynix-config-official">
        <div>
          <span>USD/CNY</span>
          <strong>{Number.isFinite(usdCny) ? usdCny.toFixed(4) : '-'}</strong>
          <small>{data?.fx?.usdCnySource === 'yahoo' ? 'Yahoo 实时' : (data?.fx?.usdCnyError || '汇率')}</small>
        </div>
        <div>
          <span>USD/KRW</span>
          <strong>{Number.isFinite(usdKrw) ? usdKrw.toFixed(2) : '-'}</strong>
          <small>{data?.fx?.usdKrwSource === 'yahoo' ? 'Yahoo 实时' : (data?.fx?.usdKrwError || '汇率')}</small>
        </div>
        <label>
          基线汇率
          <input
            value={baselineRatio}
            onChange={(event) => {
              baselineLockedRef.current = true;
              setBaselineRatio(event.target.value);
            }}
            placeholder="首次自动锁定"
          />
        </label>
        <button type="button" onClick={() => loadMcap()} disabled={loading}>{loading ? '刷新中' : '立即刷新'}</button>
        <div>
          <span>同步频率</span>
          <strong>5 秒</strong>
          <small>最后更新 {lastText}</small>
        </div>
      </section>

      {message && <div className="primary-arb-message">{message}</div>}

      <section className="cxmt-hynix-metrics">
        <div>
          <span>长鑫 688825</span>
          <strong>{Number.isFinite(cxmtPrice) ? `¥${cxmtPrice.toFixed(2)}` : '-'}</strong>
          <small>{data?.cxmt?.source === 'eastmoney' ? '东方财富官方' : (data?.cxmt?.source === 'yahoo-price×shares' ? 'Yahoo 股价×股本' : (data?.cxmt?.source || '等待数据'))} · {data?.cxmt?.name || '长鑫科技'}</small>
        </div>
        <div>
          <span>长鑫美元市值</span>
          <strong>{formatUsdYi(cxmtMcapUsdYi)}</strong>
          <small>官方市值约合 {formatRmbYi(cxmtMcapCnyYi)}</small>
        </div>
        <div>
          <span>海力士 000660</span>
          <strong>{Number.isFinite(hynixPrice) ? `₩${Math.round(hynixPrice).toLocaleString('zh-CN')}` : '-'}</strong>
          <small>{data?.hynix?.source === 'eastmoney' ? '东方财富官方' : (data?.hynix?.source === 'yahoo-price×shares' ? 'Yahoo 股价×股本' : (data?.hynix?.source || '等待数据'))} · {data?.hynix?.name || 'SK海力士'}</small>
        </div>
        <div>
          <span>海力士美元市值</span>
          <strong>{formatUsdYi(hynixMcapUsdYi)}</strong>
          <small>
            约合 {formatRmbYi(hynixMcapCnyYi)}
            {Number.isFinite(hynixMcapKrwYi) ? ` · 韩元市值 ${(hynixMcapKrwYi / 10000).toFixed(2)} 万亿` : ''}
          </small>
        </div>
        <div className={positive ? 'premium' : negative ? 'discount' : ''}>
          <span>市值汇率 CXMT/Hynix</span>
          <strong>{formatFxRatio(fxRatio)}</strong>
          <small>基线 {formatFxRatio(baseline)}</small>
        </div>
        <div className={positive ? 'premium' : negative ? 'discount' : ''}>
          <span>相对基线偏离</span>
          <strong>{Number.isFinite(deviationPct) ? `${deviationPct >= 0 ? '+' : ''}${(deviationPct * 100).toFixed(3)}%` : '-'}</strong>
          <small>{statusText}</small>
        </div>
      </section>

      <section className="cxmt-hynix-ref">
        <div>
          <span>对照 · HYPE CXMT</span>
          <strong>{formatPrice(hypeCxmtPrice, 3)}</strong>
          <small>隐含约 {formatUsdYi(hypeImpliedUsdYi)}（非官方结算）</small>
        </div>
        <div>
          <span>对照 · Binance SKHYNIX</span>
          <strong>{formatPrice(binancePrice, 2)}</strong>
          <small>永续报价，仅作盘中参考</small>
        </div>
      </section>

      <section className="cxmt-hynix-chart-card">
        <div className="cxmt-hynix-chart-head">
          <div>
            <h2>官方市值汇率走势</h2>
            <p>虚线为基线汇率；上涨表示长鑫相对海力士市值抬升。</p>
          </div>
          <span className={positive ? 'premium' : negative ? 'discount' : ''}>{statusText}</span>
        </div>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-label="长鑫海力士官方市值汇率走势图">
          {Number.isFinite(baselineY) && <line x1="0" x2="100" y1={baselineY} y2={baselineY} />}
          <polyline points={sparkPoints} />
        </svg>
      </section>

      <section className="cxmt-hynix-playbook">
        <div>
          <b>数据源</b>
          <p>市值来自东方财富股票快照；USD/CNY、USD/KRW 来自 Yahoo。两边都折成美元再比。</p>
        </div>
        <div>
          <b>和衍生品差在哪</b>
          <p>HYPE / Binance 是预期或永续价，会和官方正股市值有偏离；本页主指标以官方市值为准。</p>
        </div>
        <div>
          <b>怎么看汇率</b>
          <p>汇率变大=长鑫相对更贵；变小=长鑫相对更便宜。可手动改基线锁定你的观察锚点。</p>
        </div>
      </section>
    </div>
  );
}

function UnitreeSpreadPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [usdCny, setUsdCny] = useState(String(UNITREE_DEFAULT_USD_CNY));
  const [totalSharesYi, setTotalSharesYi] = useState(String(UNITREE_DEFAULT_TOTAL_SHARES_YI));

  async function loadUnitree(silent = false) {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ hype: UNITREE_HYPE_SYMBOL });
      const resp = await fetch(api(`/api/hype-asset?${params.toString()}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `加载失败 ${resp.status}`);
      setData(body.data || null);
      setMessage('');
    } catch (err) {
      setMessage(err?.message || '宇树估值数据读取失败');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadUnitree();
    const timer = window.setInterval(() => loadUnitree(true), 3000);
    return () => window.clearInterval(timer);
  }, []);

  const fx = Number(usdCny);
  const sharesYi = Number(totalSharesYi);
  const hypePrice = Number(data?.price);
  const hypeFunding = Number(data?.funding);
  const rmbSharePrice = Number.isFinite(hypePrice) && Number.isFinite(fx) ? hypePrice * fx : null;
  const impliedMarketCapYi = Number.isFinite(rmbSharePrice) && Number.isFinite(sharesYi) ? rmbSharePrice * sharesYi : null;
  const impliedUsdMarketCapYi = Number.isFinite(hypePrice) && Number.isFinite(sharesYi) ? hypePrice * sharesYi : null;
  const ipoUsd = Number.isFinite(fx) && fx > 0 ? UNITREE_IPO_PRICE_CNY / fx : null;
  const ipoPremiumPct = Number.isFinite(rmbSharePrice) && UNITREE_IPO_PRICE_CNY > 0
    ? ((rmbSharePrice - UNITREE_IPO_PRICE_CNY) / UNITREE_IPO_PRICE_CNY) * 100
    : null;
  const ipoMarketCapYi = UNITREE_IPO_PRICE_CNY * UNITREE_DEFAULT_TOTAL_SHARES_YI;
  const updatedAt = data?.syncedAt || data?.fetchedAt
    ? new Date(data.syncedAt || data.fetchedAt).toLocaleTimeString('zh-CN', { hour12: false })
    : '-';
  const premiumLabel = Number.isFinite(ipoPremiumPct)
    ? `${ipoPremiumPct >= 0 ? '+' : ''}${ipoPremiumPct.toFixed(2)}%`
    : '-';

  return (
    <div className="cxmt-spread-page">
      <section className="cxmt-hero">
        <div>
          <span className="cxmt-eyebrow">UNITREE Valuation Monitor</span>
          <h1>宇树估值监控</h1>
          <p>
            读取 Hyperliquid xyz:UNITREE 盘前价格，并与已公告发行价对比溢价。
            宇树科技（{UNITREE_IPO_CODE}）发行价 {UNITREE_IPO_PRICE_CNY.toFixed(2)} 元/股，网上/网下申购日 {UNITREE_IPO_SUBSCRIBE_DATE}。
            HYPE 美元价 × 汇率 = 隐含人民币股价；相对 IPO 溢价 =（隐含人民币股价 − 发行价）÷ 发行价。
          </p>
        </div>
        <div className="cxmt-hero-actions">
          <a href={UNITREE_DOCS_URL} target="_blank" rel="noreferrer">打开 trade.xyz 规格</a>
          <a href={UNITREE_HYPE_URL} target="_blank" rel="noreferrer">打开 HYPE UNITREE</a>
        </div>
      </section>

      <section className="cxmt-formula-card">
        <div>
          <b>IPO 最新信息</b>
          <p>
            2026-08-06 公告确定发行价 {UNITREE_IPO_PRICE_CNY.toFixed(2)} 元/股（发行市盈率约 219.23 倍），发行后总市值约 {formatRmbYi(ipoMarketCapYi)}；
            发行股数 4,044.6434 万股，申购日 {UNITREE_IPO_SUBSCRIBE_DATE}。
          </p>
        </div>
        <div>
          <b>溢价公式</b>
          <p>
            隐含人民币股价 = HYPE × USD/CNH；
            相对 IPO 溢价 =（隐含人民币股价 − {UNITREE_IPO_PRICE_CNY.toFixed(2)}）÷ {UNITREE_IPO_PRICE_CNY.toFixed(2)}。
            等价于把发行价折成美元后再和 HYPE 比。
          </p>
        </div>
        <div>
          <b>当前溢价</b>
          <p>
            {Number.isFinite(ipoPremiumPct)
              ? `HYPE ${formatPrice(hypePrice, 3)}（约 ${formatCny(rmbSharePrice, 2)}/股）相对发行价 ${UNITREE_IPO_PRICE_CNY.toFixed(2)} 元，溢价 ${premiumLabel}。`
              : '等待 HYPE 价格后自动计算溢价。'}
          </p>
        </div>
      </section>

      <section className="cxmt-config">
        <label>
          美元兑人民币
          <input value={usdCny} onChange={(event) => setUsdCny(event.target.value)} />
        </label>
        <label>
          总股本（亿股）
          <input value={totalSharesYi} onChange={(event) => setTotalSharesYi(event.target.value)} />
        </label>
        <button type="button" onClick={() => loadUnitree()} disabled={loading}>{loading ? '刷新中' : '立即刷新'}</button>
        <div>
          <span>同步频率</span>
          <strong>3 秒</strong>
          <small>最后更新 {updatedAt}</small>
        </div>
      </section>

      {message && <div className="primary-arb-message">{message}</div>}

      <section className="cxmt-metrics">
        <div>
          <span>HYPE UNITREE</span>
          <strong>{formatPrice(hypePrice, 3)}</strong>
          <small>{data?.source === 'hyperliquid' ? 'Hyperliquid xyz 实时' : (data?.error || '等待 HYPE 数据')}</small>
        </div>
        <div>
          <span>折算人民币股价</span>
          <strong>{formatCny(rmbSharePrice, 2)}</strong>
          <small>HYPE × USD/CNH {Number.isFinite(fx) ? fx : '-'}</small>
        </div>
        <div>
          <span>相对 IPO 溢价</span>
          <strong>{premiumLabel}</strong>
          <small>发行价 {UNITREE_IPO_PRICE_CNY.toFixed(2)} 元 ≈ ${Number.isFinite(ipoUsd) ? ipoUsd.toFixed(3) : '-'}</small>
        </div>
        <div>
          <span>隐含人民币市值</span>
          <strong>{formatRmbYi(impliedMarketCapYi)}</strong>
          <small>IPO 发行市值约 {formatRmbYi(ipoMarketCapYi)}</small>
        </div>
      </section>

      <section className="cxmt-results">
        <div className="cxmt-results-head">
          <div>
            <h2>估值速览</h2>
            <p>HYPE 隐含价对比已公告发行价；汇率可按离岸 USD/CNH 手动调整。</p>
          </div>
          <span>相对 IPO：{premiumLabel}</span>
        </div>
        <div className="cxmt-table">
          <article className="cxmt-row active">
            <div className="cxmt-market-title">
              <div>
                <a href={UNITREE_HYPE_URL} target="_blank" rel="noreferrer">xyz:UNITREE</a>
                <span>美元每股隐含价</span>
              </div>
            </div>
            <div className="cxmt-prob">
              <strong>{formatPrice(hypePrice, 3)}</strong>
              <span>HYPE</span>
            </div>
            <div className="cxmt-valuation">
              <b>折算人民币股价 {formatCny(rmbSharePrice, 2)}</b>
              <p>
                相对发行价 {UNITREE_IPO_PRICE_CNY.toFixed(2)} 元溢价 {premiumLabel}
                （IPO 折美元约 ${Number.isFinite(ipoUsd) ? ipoUsd.toFixed(3) : '-'}）。
              </p>
            </div>
            <div className="cxmt-volume">
              <span>人民币市值</span>
              <strong>{formatRmbYi(impliedMarketCapYi)}</strong>
            </div>
          </article>
          <article className="cxmt-row muted">
            <div className="cxmt-market-title">
              <div>
                <span>科创板 {UNITREE_IPO_CODE}</span>
                <span>发行价 / 申购日 {UNITREE_IPO_SUBSCRIBE_DATE}</span>
              </div>
            </div>
            <div className="cxmt-prob">
              <strong>{UNITREE_IPO_PRICE_CNY.toFixed(2)}</strong>
              <span>元/股</span>
            </div>
            <div className="cxmt-valuation">
              <b>发行市值约 {formatRmbYi(ipoMarketCapYi)}</b>
              <p>公告发行市盈率约 219.23 倍；战略配售含 DeepSeek、腾讯等。HYPE 资金费率 {Number.isFinite(hypeFunding) ? formatRate(hypeFunding, 4) : '-'}。</p>
            </div>
            <div className="cxmt-volume">
              <span>24h 名义成交</span>
              <strong>{formatCompactUsd(data?.dayNtlVlm)}</strong>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}

function HynixSpreadPanel() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [binanceLive, setBinanceLive] = useState(null);
  const [binanceWsStatus, setBinanceWsStatus] = useState('连接中');
  const [symbols, setSymbols] = useState({ binance: 'SKHYNIXUSDT.P', hype: 'xyz:SKHY' });

  async function loadSpread(silent = false) {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({
        binance: symbols.binance,
        hype: symbols.hype,
      });
      const resp = await fetch(api(`/api/hynix-spread?${params.toString()}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `加载失败 ${resp.status}`);
      const next = body.data || null;
      setData(next);
      if (Number.isFinite(Number(next?.spreadPct))) {
        setHistory((prev) => [...prev.slice(-79), {
          ts: next.syncedAt || Date.now(),
          spreadPct: Number(next.spreadPct),
          spread: Number(next.spread),
        }]);
      }
      setMessage('');
    } catch (err) {
      setMessage(err?.message || '海力士价差读取失败');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadSpread();
    const timer = window.setInterval(() => loadSpread(true), 3000);
    return () => window.clearInterval(timer);
  }, [symbols.binance, symbols.hype]);

  useEffect(() => {
    const cleanSymbol = String(symbols.binance || 'SKHYNIXUSDT.P').trim().toUpperCase().replace(/\.P$/i, '');
    if (!cleanSymbol) return undefined;
    let stopped = false;
    let reconnectTimer = 0;
    let ws = null;

    const connect = () => {
      if (stopped) return;
      setBinanceWsStatus('连接中');
      try {
        const streamSymbol = cleanSymbol.toLowerCase();
        ws = new WebSocket(`wss://fstream.binance.com/stream?streams=${streamSymbol}@ticker/${streamSymbol}@markPrice@1s`);
        ws.onopen = () => {
          if (!stopped) setBinanceWsStatus('已连接');
        };
        ws.onmessage = (event) => {
          try {
            const raw = JSON.parse(event.data);
            const payload = raw?.data || raw;
            const price = Number(payload.c || payload.markPrice || payload.p || payload.lastPrice || payload.indexPrice);
            if (Number.isFinite(price) && price > 0) {
              setBinanceLive({
                symbol: cleanSymbol,
                displaySymbol: `${cleanSymbol}.P`,
                price,
                priceChangePercent: Number(payload.P || 0) / 100,
                volume: Number(payload.v || 0),
                quoteVolume: Number(payload.q || 0),
                source: payload.markPrice ? 'binance-mark-ws' : 'binance-ws',
                fetchedAt: Date.now(),
              });
            }
          } catch (_) {}
        };
        ws.onerror = () => {
          if (!stopped) setBinanceWsStatus('连接异常');
        };
        ws.onclose = () => {
          if (stopped) return;
          setBinanceWsStatus('重连中');
          reconnectTimer = window.setTimeout(connect, 1800);
        };
      } catch (_) {
        if (!stopped) {
          setBinanceWsStatus('连接失败');
          reconnectTimer = window.setTimeout(connect, 2500);
        }
      }
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [symbols.binance]);

  useEffect(() => {
    const cleanSymbol = String(symbols.binance || 'SKHYNIXUSDT.P').trim().toUpperCase().replace(/\.P$/i, '');
    if (!cleanSymbol) return undefined;
    let stopped = false;

    async function loadBrowserBinancePrice() {
      const endpoints = [
        `https://www.binance.com/fapi/v1/ticker/24hr?symbol=${encodeURIComponent(cleanSymbol)}`,
        `https://fapi.binance.com/fapi/v1/ticker/24hr?symbol=${encodeURIComponent(cleanSymbol)}`,
      ];
      for (const endpoint of endpoints) {
        try {
          const resp = await fetch(endpoint, {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          const payload = await resp.json().catch(() => ({}));
          if (!resp.ok) throw new Error(payload?.msg || `Binance ${resp.status}`);
          const price = Number(payload.lastPrice || payload.markPrice || payload.weightedAvgPrice);
          if (Number.isFinite(price) && price > 0 && !stopped) {
            setBinanceLive((prev) => {
              const prevAt = Number(prev?.fetchedAt || 0);
              if (prev?.source?.includes('ws') && Date.now() - prevAt < 1800) return prev;
              return {
                symbol: cleanSymbol,
                displaySymbol: `${cleanSymbol}.P`,
                price,
                priceChangePercent: Number(payload.priceChangePercent || 0) / 100,
                volume: Number(payload.volume || 0),
                quoteVolume: Number(payload.quoteVolume || 0),
                source: endpoint.includes('www.binance.com') ? 'binance-browser-www' : 'binance-browser-rest',
                fetchedAt: Date.now(),
              };
            });
            setBinanceWsStatus((prev) => (prev === '已连接' ? prev : 'REST 兜底'));
            return;
          }
        } catch (_) {}
      }
    }

    loadBrowserBinancePrice();
    const timer = window.setInterval(loadBrowserBinancePrice, 2500);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [symbols.binance]);

  const restBinancePriceRaw = Number(data?.binance?.price);
  const restBinancePrice = Number.isFinite(restBinancePriceRaw) && restBinancePriceRaw > 0 ? restBinancePriceRaw : null;
  const binancePrice = Number.isFinite(Number(binanceLive?.price)) && Number(binanceLive?.price) > 0
    ? Number(binanceLive.price)
    : restBinancePrice;
  const hypePriceRaw = Number(data?.hypePrice);
  const hypePrice = Number.isFinite(hypePriceRaw) && hypePriceRaw > 0 ? hypePriceRaw : null;
  const normalizedRaw = Number(data?.binanceNormalized);
  const normalized = Number.isFinite(normalizedRaw) && normalizedRaw > 0 ? normalizedRaw : null;
  const effectiveNormalized = Number.isFinite(binancePrice) && binancePrice > 0 ? binancePrice / 10 : normalized;
  const spread = Number.isFinite(hypePrice) && Number.isFinite(effectiveNormalized) && effectiveNormalized > 0 ? hypePrice - effectiveNormalized : Number(data?.spread);
  const spreadPct = Number.isFinite(spread) && Number.isFinite(effectiveNormalized) && effectiveNormalized > 0 ? spread / effectiveNormalized : Number(data?.spreadPct);
  const positive = Number.isFinite(spread) && spread > 0;
  const negative = Number.isFinite(spread) && spread < 0;
  const statusText = positive ? 'SKHY 正溢价' : negative ? 'SKHY 负溢价' : '价差持平';
  const lastText = data?.syncedAt ? new Date(data.syncedAt).toLocaleTimeString('zh-CN', { hour12: false }) : '-';

  useEffect(() => {
    if (!Number.isFinite(spreadPct)) return;
    setHistory((prev) => [...prev.slice(-79), {
      ts: Date.now(),
      spreadPct: Number(spreadPct),
      spread: Number(spread),
    }]);
  }, [spreadPct, spread]);

  const chartPoints = history.length >= 2 ? history : [
    { ts: Date.now() - 60_000, spreadPct: 0 },
    { ts: Date.now(), spreadPct: Number.isFinite(spreadPct) ? spreadPct : 0 },
  ];
  const min = Math.min(...chartPoints.map((item) => item.spreadPct), 0);
  const max = Math.max(...chartPoints.map((item) => item.spreadPct), 0);
  const range = max - min || 0.01;
  const sparkPoints = chartPoints.map((item, index) => {
    const x = chartPoints.length <= 1 ? 0 : (index / (chartPoints.length - 1)) * 100;
    const y = 84 - ((item.spreadPct - min) / range) * 68;
    return `${x},${y}`;
  }).join(' ');
  const zeroY = 84 - ((0 - min) / range) * 68;

  return (
    <div className="hynix-spread-page">
      <section className="hynix-hero">
        <div>
          <span className="hynix-eyebrow">Spread Convergence Monitor</span>
          <h1>海力士价差收敛</h1>
          <p>同时监控 Binance 美股海力士永续与 Hyperliquid 韩股海力士永续。按 1:10 折算：SKHY 高于 SKHYNIX / 10 为正溢价，低于则为负溢价。</p>
        </div>
        <a href="https://app.hyperliquid.xyz/trade/xyz:SKHY" target="_blank" rel="noreferrer">打开 HYPE SKHY</a>
      </section>

      <section className="hynix-config">
        <label>
          Binance 合约
          <input value={symbols.binance} onChange={(event) => setSymbols((prev) => ({ ...prev, binance: event.target.value }))} />
        </label>
        <label>
          Hyperliquid 合约
          <input value={symbols.hype} onChange={(event) => setSymbols((prev) => ({ ...prev, hype: event.target.value }))} />
        </label>
        <button type="button" onClick={() => loadSpread()} disabled={loading}>{loading ? '刷新中' : '立即刷新'}</button>
        <div>
          <span>刷新频率</span>
          <strong>3 秒</strong>
          <small>最后更新 {lastText}</small>
        </div>
      </section>

      {message && <div className="primary-arb-message">{message}</div>}

      <section className="hynix-metrics">
        <div>
          <span>Binance {data?.binance?.displaySymbol || symbols.binance}</span>
          <strong>{formatPrice(binancePrice, 3)}</strong>
          <small>折算成 SKHY：{formatPrice(effectiveNormalized, 3)} · {binanceLive?.source || 'Binance'} {binanceWsStatus}</small>
        </div>
        <div>
          <span>HYPE {data?.hype?.displaySymbol || symbols.hype}</span>
          <strong>{formatPrice(hypePrice, 3)}</strong>
          <small>{data?.hype?.source === 'hyperliquid' ? 'Hyperliquid 实时' : (data?.hype?.error || '等待数据')}</small>
        </div>
        <div className={positive ? 'premium' : negative ? 'discount' : ''}>
          <span>当前价差</span>
          <strong>{Number.isFinite(spread) ? `${spread >= 0 ? '+' : ''}$${spread.toFixed(3)}` : '-'}</strong>
          <small>HYPE - Binance / 10</small>
        </div>
        <div className={positive ? 'premium' : negative ? 'discount' : ''}>
          <span>价差百分比</span>
          <strong>{Number.isFinite(spreadPct) ? `${spreadPct >= 0 ? '+' : ''}${(spreadPct * 100).toFixed(3)}%` : '-'}</strong>
          <small>{statusText}</small>
        </div>
      </section>

      <section className="hynix-chart-card">
        <div className="hynix-chart-head">
          <div>
            <h2>实时价差走势</h2>
            <p>围绕 0% 收敛，正值代表 HYPE SKHY 更贵，负值代表 HYPE SKHY 更便宜。</p>
          </div>
          <span className={positive ? 'premium' : negative ? 'discount' : ''}>{statusText}</span>
        </div>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-label="海力士价差走势图">
          <line x1="0" x2="100" y1={zeroY} y2={zeroY} />
          <polyline points={sparkPoints} />
        </svg>
      </section>

      <section className="hynix-playbook">
        <div>
          <b>正溢价</b>
          <p>SKHY 高于 SKHYNIX / 10。若判断价差会收敛，常见思路是做空高估侧、做多低估侧。</p>
        </div>
        <div>
          <b>负溢价</b>
          <p>SKHY 低于 SKHYNIX / 10。若判断价差会收敛，方向相反，重点看流动性与资金费率。</p>
        </div>
        <div>
          <b>当前换算</b>
          <p>1 份 SKHYNIX ≈ 10 份 SKHY，所以比较基准是 Binance 价格除以 10。</p>
        </div>
      </section>
    </div>
  );
}

export default function PrimaryArbitragePage() {
  const [monitors, setMonitors] = useState(() => loadStoredMonitors());
  const [activeMonitorId, setActiveMonitorId] = useState(() => monitors[0]?.id || '');
  const activeMonitor = useMemo(
    () => monitors.find((item) => item.id === activeMonitorId) || monitors[0] || null,
    [monitors, activeMonitorId],
  );
  const [form, setForm] = useState(() => ({
    name: activeMonitor?.name || '',
    marketUrl: activeMonitor?.marketUrl || DEFAULT_MARKET_URL,
    dexUrl: activeMonitor?.dexUrl || DEFAULT_DEX_URL,
    hypeSymbol: activeMonitor?.hypeSymbol || DEFAULT_HYPE_SYMBOL,
  }));
  const [dashboardsById, setDashboardsById] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState({
    currentSpotPrice: 0.162,
    currentFdv: 162_690_000,
    totalSupply: 1_000_000_000,
    spotBuyFee: 0.005,
    spotSellFee: 0.005,
    predictTakerFeeRate: 0.02,
  });
  const [makerCalc, setMakerCalc] = useState({ priceCents: '', shares: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [pageMode, setPageMode] = useState('primary');
  const [settingsTouched, setSettingsTouched] = useState(false);
  const [wsStatus, setWsStatus] = useState('连接中');
  const [lastLiveAt, setLastLiveAt] = useState(0);
  const settingsTouchedRef = useRef(false);
  const activeMonitorIdRef = useRef(activeMonitorId);

  useEffect(() => {
    activeMonitorIdRef.current = activeMonitorId;
  }, [activeMonitorId]);

  useEffect(() => {
    saveStoredMonitors(monitors);
  }, [monitors]);

  useEffect(() => {
    if (!activeMonitor) return;
    setForm({
      name: activeMonitor.name || '',
      marketUrl: activeMonitor.marketUrl || DEFAULT_MARKET_URL,
      dexUrl: activeMonitor.dexUrl || DEFAULT_DEX_URL,
      hypeSymbol: activeMonitor.hypeSymbol || '',
    });
    const cached = dashboardsById[activeMonitor.id];
    setDashboard(cached || null);
    setMessage('');
    setWsStatus('切换中');
    if (cached) {
      settingsTouchedRef.current = false;
      setSettingsTouched(false);
      applySpot(cached.spot, true);
    }
  }, [activeMonitor?.id]);

  useEffect(() => {
    settingsTouchedRef.current = settingsTouched;
  }, [settingsTouched]);

  const outcomes = useMemo(() => (dashboard?.outcomes || []).map((item) => {
    const threshold = calculateThreshold(item.targetFdv, item.noPrice, settings) || item.threshold || null;
    return { ...item, threshold };
  }), [dashboard, settings]);

  const summary = useMemo(() => {
    const active = outcomes.filter((item) => item.threshold?.status === 'ACTIVE').length;
    const near = outcomes.filter((item) => item.threshold?.status === 'NEAR').length;
    const best = outcomes
      .filter((item) => item.threshold)
      .sort((a, b) => Number(a.threshold.gapPct || 0) - Number(b.threshold.gapPct || 0))[0];
    return { active, near, best };
  }, [outcomes]);

  function monitorRequestUrl(monitor) {
    const slug = parseSlug(monitor?.marketUrl || monitor?.slug || DEFAULT_MARKET_URL);
    const params = new URLSearchParams({ slug });
    const dex = String(monitor?.dexUrl || '').trim();
    const hype = String(monitor?.hypeSymbol || '').trim();
    if (dex) params.set('dex', dex);
    if (hype) params.set('hype', hype);
    return api(`/api/primary-arbitrage?${params.toString()}`);
  }

  function monitorWsUrl(monitor) {
    const slug = parseSlug(monitor?.marketUrl || monitor?.slug || DEFAULT_MARKET_URL);
    const params = new URLSearchParams({ slug });
    const dex = String(monitor?.dexUrl || '').trim();
    const hype = String(monitor?.hypeSymbol || '').trim();
    if (dex) params.set('dex', dex);
    if (hype) params.set('hype', hype);
    return wsApi(`/api/primary-arbitrage/ws?${params.toString()}`);
  }

  function updateActiveDashboard(updater) {
    setDashboard((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (activeMonitor?.id && next) {
        setDashboardsById((old) => ({ ...old, [activeMonitor.id]: next }));
      }
      return next;
    });
  }

  function updateMonitorDashboard(monitorId, updater) {
    setDashboardsById((old) => {
      const prev = old[monitorId] || null;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (activeMonitorIdRef.current === monitorId) {
        setDashboard(next || null);
      }
      return { ...old, [monitorId]: next };
    });
  }

  function applySpot(spot, force = false) {
    if (!spot || (!force && settingsTouchedRef.current)) return;
    setSettings((prev) => ({
      ...prev,
      currentSpotPrice: numberValue(spot.currentSpotPrice, prev.currentSpotPrice),
      currentFdv: numberValue(spot.currentFdv, prev.currentFdv),
      totalSupply: numberValue(spot.totalSupply, prev.totalSupply),
      spotBuyFee: numberValue(spot.spotBuyFee, prev.spotBuyFee),
      spotSellFee: numberValue(spot.spotSellFee, prev.spotSellFee),
      predictTakerFeeRate: numberValue(spot.predictTakerFeeRate, prev.predictTakerFeeRate),
    }));
  }

  async function loadDashboard(nextMonitor = activeMonitor, silent = false, makeActive = true) {
    if (!nextMonitor) return null;
    if (!silent) setLoading(true);
    try {
      const resp = await fetch(monitorRequestUrl(nextMonitor), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `加载失败 ${resp.status}`);
      const data = body.data ? { ...body.data, monitorId: nextMonitor.id } : null;
      updateMonitorDashboard(nextMonitor.id, data);
      if (makeActive && activeMonitorIdRef.current === nextMonitor.id) {
        setDashboard(data);
        applySpot(data?.spot);
      }
      setLastLiveAt(Date.now());
      setMessage('');
      return data;
    } catch (err) {
      if (makeActive) setMessage(err?.message || '加载失败');
      updateMonitorDashboard(nextMonitor.id, (prev) => ({
        ...(prev || {}),
        monitorId: nextMonitor.id,
        error: err?.message || '加载失败',
        syncedAt: Date.now(),
      }));
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }

  function saveCurrentMonitor() {
    const next = createMonitor({
      id: activeMonitor?.id,
      createdAt: activeMonitor?.createdAt,
      name: form.name,
      marketUrl: form.marketUrl,
      dexUrl: form.dexUrl,
      hypeSymbol: form.hypeSymbol,
      enabled: activeMonitor?.enabled !== false,
    });
    setMonitors((prev) => {
      const exists = prev.some((item) => item.id === next.id);
      return exists ? prev.map((item) => (item.id === next.id ? next : item)) : [...prev, next];
    });
    activeMonitorIdRef.current = next.id;
    setActiveMonitorId(next.id);
    try {
      localStorage.setItem(STORAGE_KEY, next.marketUrl);
    } catch (_) {}
    settingsTouchedRef.current = false;
    setSettingsTouched(false);
    loadDashboard(next, false, true);
  }

  function addNewMonitor() {
    const next = createMonitor({
      name: form.name,
      marketUrl: form.marketUrl,
      dexUrl: form.dexUrl,
      hypeSymbol: form.hypeSymbol,
    });
    setMonitors((prev) => [...prev, next]);
    activeMonitorIdRef.current = next.id;
    setActiveMonitorId(next.id);
    settingsTouchedRef.current = false;
    setSettingsTouched(false);
    loadDashboard(next, false, true);
  }

  function removeActiveMonitor() {
    if (!activeMonitor) return;
    setMonitors((prev) => {
      const next = prev.filter((item) => item.id !== activeMonitor.id);
      const fallback = next[0]?.id || '';
      activeMonitorIdRef.current = fallback;
      setActiveMonitorId(fallback);
      return next.length ? next : [createMonitor()];
    });
    setDashboardsById((prev) => {
      const next = { ...prev };
      delete next[activeMonitor.id];
      return next;
    });
  }

  useEffect(() => {
    if (activeMonitor) loadDashboard(activeMonitor);
  }, []);

  useEffect(() => {
    if (!monitors.length) return undefined;
    let cancelled = false;
    const refreshAll = async () => {
      for (const monitor of monitors.filter((item) => item.enabled !== false)) {
        if (cancelled) return;
        await loadDashboard(monitor, true, monitor.id === activeMonitorId);
      }
    };
    const timer = window.setInterval(refreshAll, 30000);
    refreshAll();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [monitors, activeMonitorId]);

  useEffect(() => {
    if (!activeMonitor) return undefined;
    const socketMonitorId = activeMonitor.id;
    let stopped = false;
    let socket = null;
    let reconnectTimer = null;
    let connectTimer = null;
    let fallbackTimer = null;
    let reconnectDelay = 1000;
    const clearConnectTimer = () => {
      if (connectTimer) window.clearTimeout(connectTimer);
      connectTimer = null;
    };
    const stopFallback = () => {
      if (fallbackTimer) window.clearInterval(fallbackTimer);
      fallbackTimer = null;
    };
    const startFallback = (reason) => {
      if (fallbackTimer) return;
      setWsStatus(`${reason}，REST刷新中`);
      loadDashboard(activeMonitor, true, true);
      fallbackTimer = window.setInterval(() => loadDashboard(activeMonitor, true, true), 10000);
    };
    const connect = () => {
      if (stopped) return;
      setWsStatus('连接中');
      socket = new WebSocket(monitorWsUrl(activeMonitor));
      connectTimer = window.setTimeout(() => {
        if (!stopped && socket?.readyState === WebSocket.CONNECTING) {
          setWsStatus('连接超时，重连中');
          try { socket.close(); } catch (_) {}
        }
      }, 8000);
      socket.onopen = () => {
        clearConnectTimer();
        reconnectDelay = 1000;
        setWsStatus('已连接');
      };
      socket.onmessage = (event) => {
        let msg = null;
        try {
          msg = JSON.parse(String(event.data || '{}'));
        } catch (_) {
          return;
        }
        if (!msg) return;
        if (msg.type === 'snapshot') {
          stopFallback();
          setWsStatus('已连接');
          updateMonitorDashboard(socketMonitorId, msg.data ? { ...msg.data, monitorId: socketMonitorId } : null);
          applySpot(msg.data?.spot);
          setLastLiveAt(Date.now());
          setMessage('');
          return;
        }
        if (msg.type === 'spot') {
          stopFallback();
          setWsStatus('已连接');
          const spot = msg.data || {};
          const hype = msg.hype || null;
          updateMonitorDashboard(socketMonitorId, (prev) => (prev ? { ...prev, spot, ...(hype ? { hype } : {}) } : prev));
          applySpot(spot);
          setLastLiveAt(Date.now());
          return;
        }
        if (msg.type === 'orderbook') {
          stopFallback();
          setWsStatus('已连接');
          const update = msg.data || {};
          updateMonitorDashboard(socketMonitorId, (prev) => (prev ? {
            ...prev,
            outcomes: (prev.outcomes || []).map((item) => (String(item.id) === String(update.marketId) ? {
              ...item,
              yesBid: update.yesBid ?? item.yesBid,
              yesAsk: update.yesAsk ?? item.yesAsk,
              noBid: update.noBid ?? item.noBid,
              noAsk: update.noAsk ?? item.noAsk,
              yesPrice: Number.isFinite(Number(update.yesPrice)) ? Number(update.yesPrice) : item.yesPrice,
              noPrice: Number.isFinite(Number(update.noPrice)) ? Number(update.noPrice) : item.noPrice,
              probability: Number.isFinite(Number(update.probability)) ? Number(update.probability) : item.probability,
              orderCount: update.orderCount ?? item.orderCount,
              lastOrderSettled: update.lastOrderSettled || item.lastOrderSettled,
              liveUpdatedAt: update.updateTimestampMs || Date.now(),
            } : item)),
          } : prev));
          setLastLiveAt(Date.now());
          return;
        }
        if (msg.type === 'market-status') {
          stopFallback();
          setWsStatus('已连接');
          const data = msg.data || {};
          const marketId = String(data.marketId || '');
          const liveOutcomes = Array.isArray(data.marketOutcomes) ? data.marketOutcomes : [];
          const yes = liveOutcomes.find((outcome) => String(outcome.label || outcome.name || '').toLowerCase() === 'yes');
          const no = liveOutcomes.find((outcome) => String(outcome.label || outcome.name || '').toLowerCase() === 'no');
          updateMonitorDashboard(socketMonitorId, (prev) => (prev ? {
            ...prev,
            outcomes: (prev.outcomes || []).map((item) => (String(item.id) === marketId ? {
              ...item,
              status: data.status || item.status,
              probability: Number.isFinite(Number(yes?.price)) ? Number(yes.price) : item.probability,
              yesPrice: Number.isFinite(Number(yes?.price)) ? Number(yes.price) : item.yesPrice,
              noPrice: Number.isFinite(Number(no?.price)) ? Number(no.price) : item.noPrice,
              liveUpdatedAt: data.tsMs || Date.now(),
            } : item)),
          } : prev));
          setLastLiveAt(Date.now());
          return;
        }
        if (msg.type === 'ws-status') {
          const labelMap = { connected: '已连接', reconnecting: '重连中', error: '异常' };
          setWsStatus(labelMap[msg.status] || msg.status || '连接中');
          return;
        }
        if (msg.type === 'error') {
          setMessage(msg.message || '实时连接异常');
        }
      };
      socket.onerror = () => {
        clearConnectTimer();
        startFallback('实时连接异常');
      };
      socket.onclose = () => {
        clearConnectTimer();
        if (stopped) return;
        startFallback('实时连接断开');
        reconnectTimer = window.setTimeout(connect, reconnectDelay);
        reconnectDelay = Math.min(60000, Math.round(reconnectDelay * 2));
      };
    };
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      clearConnectTimer();
      stopFallback();
      try { socket?.close?.(); } catch (_) {}
    };
  }, [activeMonitor?.id, activeMonitor?.marketUrl, activeMonitor?.dexUrl, activeMonitor?.hypeSymbol]);

  const title = dashboard?.title || activeMonitor?.name || '一级预测套利监控';
  const spotSource = dashboard?.spot?.source === 'dexscreener' ? 'Dexscreener 实时' : '备用默认值';
  const hype = dashboard?.hype || null;
  const hypeFunding = Number(hype?.funding);
  const hypeFundingText = Number.isFinite(hypeFunding)
    ? (hypeFunding < 0 ? '负费率，多头收取' : hypeFunding > 0 ? '正费率，多头支付' : '资金费率接近 0')
    : '资金费率暂无';
  const lastLiveText = lastLiveAt ? new Date(lastLiveAt).toLocaleTimeString('zh-CN', { hour12: false }) : '-';
  const activeMonitorDash = activeMonitor ? dashboardsById[activeMonitor.id] : null;

  return (
    <div className="primary-arb-page">
      <section className="primary-arb-mode-tabs">
        <button
          type="button"
          className={pageMode === 'primary' ? 'active' : ''}
          onClick={() => setPageMode('primary')}
        >
          一级预测套利
        </button>
        <button
          type="button"
          className={pageMode === 'hynix' ? 'active' : ''}
          onClick={() => setPageMode('hynix')}
        >
          海力士价差收敛
        </button>
        <button
          type="button"
          className={pageMode === 'cxmt-hynix' ? 'active' : ''}
          onClick={() => setPageMode('cxmt-hynix')}
        >
          长鑫海力士汇率
        </button>
        <button
          type="button"
          className={pageMode === 'cxmt' ? 'active' : ''}
          onClick={() => setPageMode('cxmt')}
        >
          长鑫价差套利
        </button>
        <button
          type="button"
          className={pageMode === 'unitree' ? 'active' : ''}
          onClick={() => setPageMode('unitree')}
        >
          宇树估值监控
        </button>
      </section>

      {pageMode === 'hynix' ? (
        <HynixSpreadPanel />
      ) : pageMode === 'cxmt-hynix' ? (
        <CxmtHynixMcapPanel />
      ) : pageMode === 'cxmt' ? (
        <CxmtSpreadPanel />
      ) : pageMode === 'unitree' ? (
        <UnitreeSpreadPanel />
      ) : (
        <>
      <section className="primary-arb-hero">
        <div className="primary-arb-hero-main">
          {dashboard?.imageUrl ? (
            <img src={dashboard.imageUrl} alt="" />
          ) : (
            <div className="primary-arb-fallback-icon">CAT</div>
          )}
          <div>
            <div className="primary-arb-meta-line">
              <span>总成交量 <strong>{formatCompactUsd(dashboard?.totalVolumeUsd)}</strong></span>
              <span>结束 {formatDateTime(dashboard?.endsAt)}</span>
              <span className="primary-arb-readonly">只读预览，不会下单</span>
            </div>
            <h1>{title}</h1>
          </div>
        </div>
        <a className="primary-arb-share" href={dashboard?.url || activeMonitor?.marketUrl || DEFAULT_MARKET_URL} target="_blank" rel="noreferrer">
          打开官方市场
        </a>
      </section>

      <section className="primary-arb-controls">
        <div>
          <label>添加 / 编辑监控市场</label>
          <div className="primary-arb-monitor-form">
            <input placeholder="备注名，例如 CASHCAT FDV" value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            <input placeholder="Predict 市场链接 / slug" value={form.marketUrl} onChange={(event) => setForm((prev) => ({ ...prev, marketUrl: event.target.value }))} />
            <input placeholder="Dexscreener 链接，例如 https://dexscreener.com/robinhood/..." value={form.dexUrl} onChange={(event) => setForm((prev) => ({ ...prev, dexUrl: event.target.value }))} />
            <input placeholder="HYPE 币种，可留空，例如 CASHCAT" value={form.hypeSymbol} onChange={(event) => setForm((prev) => ({ ...prev, hypeSymbol: event.target.value.toUpperCase() }))} />
          </div>
          <div className="primary-arb-input-row">
            <button type="button" onClick={saveCurrentMonitor} disabled={loading}>{loading ? '加载中' : '保存并读取'}</button>
            <button type="button" onClick={addNewMonitor} disabled={loading}>新增监控</button>
            <button type="button" className="primary-arb-danger-button" onClick={removeActiveMonitor} disabled={!activeMonitor || monitors.length <= 1}>删除当前</button>
          </div>
          <p>每个监控项独立绑定 Predict 市场、Dexscreener 币对和可选 HYPE 币种；HYPE 留空或没搜到时不会同步 HYPE 价格。</p>
        </div>
        <div className="primary-arb-kpis">
          <div><strong>{monitors.length}</strong><span>监控市场</span></div>
          <div><strong>{outcomes.length}</strong><span>子市场</span></div>
          <div><strong>{summary.active}</strong><span>已进入套利区</span></div>
          <div><strong>{summary.best ? formatPercent(summary.best.threshold?.gapPct, 2) : '-'}</strong><span>最近还需下跌</span></div>
        </div>
      </section>

      <section className="primary-arb-monitor-strip">
        {monitors.map((monitor) => {
          const item = dashboardsById[monitor.id] || {};
          const count = Array.isArray(item.outcomes) ? item.outcomes.length : 0;
          const active = (item.outcomes || []).filter((outcome) => outcome.threshold?.status === 'ACTIVE').length;
          return (
            <button
              key={monitor.id}
              type="button"
              className={`primary-arb-monitor-card ${monitor.id === activeMonitor?.id ? 'active' : ''}`}
              onClick={() => setActiveMonitorId(monitor.id)}
            >
              <span>{monitor.name || item.title || parseSlug(monitor.marketUrl)}</span>
              <small>{count} 子市场 · {active} 套利 · {monitor.hypeSymbol ? `HYPE ${monitor.hypeSymbol}` : '无 HYPE'}</small>
              {item.error && <em>{item.error}</em>}
            </button>
          );
        })}
      </section>

      {message && <div className="primary-arb-message">{message}</div>}

      <section className="primary-arb-spotbar">
        <div>
          <span>当前现货</span>
          <strong>{formatPrice(settings.currentSpotPrice, 4)}</strong>
          <small>{spotSource}</small>
        </div>
        <div className="primary-arb-hype-card">
          <span>{hype?.displaySymbol ? `HYPE ${hype.displaySymbol}` : 'HYPE 未同步'}</span>
          <strong>{formatPrice(hype?.price, 3)}</strong>
          <small className={Number.isFinite(hypeFunding) && hypeFunding < 0 ? 'funding-negative' : Number.isFinite(hypeFunding) && hypeFunding > 0 ? 'funding-positive' : ''}>
            {hype?.source === 'disabled' ? '该监控项未填写 HYPE 币种' : hype?.source === 'not_found' ? 'HYPE 未找到该币种' : `资金 ${formatRate(hypeFunding)}/h · ${hypeFundingText}`}
          </small>
        </div>
        <div>
          <span>当前 FDV</span>
          <strong>{formatCompactUsd(settings.currentFdv)}</strong>
          <small>来自 Dexscreener pair</small>
        </div>
        <div>
          <span>Dex 24h 成交</span>
          <strong>{formatCompactUsd(dashboard?.spot?.volume24h)}</strong>
          <small>流动性 {formatCompactUsd(dashboard?.spot?.liquidityUsd)}</small>
        </div>
        <div>
          <span>计算假设</span>
          <strong>Predict 吃单 2%，现货买/卖各 0.5%</strong>
          <small>HYPE 市价 {formatRate(hype?.marketBuyFee)} · 挂单 {formatRate(hype?.makerBuyFee)}</small>
        </div>
      </section>

      <section className="primary-arb-settings">
        <label>
          现货价格
          <input
            type="number"
            step="0.0001"
            value={settings.currentSpotPrice}
            onChange={(event) => {
              const currentSpotPrice = numberValue(event.target.value, settings.currentSpotPrice);
              setSettingsTouched(true);
              setSettings((prev) => ({ ...prev, currentSpotPrice, currentFdv: currentSpotPrice * Number(prev.totalSupply || 0) }));
            }}
          />
        </label>
        <label>
          当前 FDV
          <input
            type="number"
            step="1000"
            value={settings.currentFdv}
            onChange={(event) => {
              setSettingsTouched(true);
              setSettings((prev) => ({ ...prev, currentFdv: numberValue(event.target.value, prev.currentFdv) }));
            }}
          />
        </label>
        <label>
          总供应量
          <input
            type="number"
            step="1"
            value={settings.totalSupply}
            onChange={(event) => {
              const totalSupply = numberValue(event.target.value, settings.totalSupply);
              setSettingsTouched(true);
              setSettings((prev) => ({ ...prev, totalSupply, currentFdv: Number(prev.currentSpotPrice || 0) * totalSupply }));
            }}
          />
        </label>
        <button
          type="button"
          className="primary-arb-dex-button"
          disabled={!settingsTouched || !dashboard?.spot}
          onClick={() => {
            settingsTouchedRef.current = false;
            setSettingsTouched(false);
            applySpot((activeMonitorDash || dashboard)?.spot, true);
          }}
        >
          恢复 Dexscreener 实时价
        </button>
      </section>

      <section className="primary-arb-maker-calc">
        <div>
          <b>Predict 挂单散单计算器</b>
          <p>按挂单买 NO 无手续费计算：输入你的实际成交价格和 shares，自动反推当前价需要配多少 U 现货才能覆盖 NO 成本。</p>
        </div>
        <label>
          NO 挂单价（¢）
          <input
            type="number"
            step="0.1"
            min="0"
            max="99.9"
            placeholder="例如 40"
            value={makerCalc.priceCents}
            onChange={(event) => setMakerCalc((prev) => ({ ...prev, priceCents: event.target.value }))}
          />
        </label>
        <label>
          成交 shares
          <input
            type="number"
            step="0.01"
            min="0"
            placeholder="例如 37.5"
            value={makerCalc.shares}
            onChange={(event) => setMakerCalc((prev) => ({ ...prev, shares: event.target.value }))}
          />
        </label>
      </section>

      <section className="primary-arb-results">
        <div className="primary-arb-results-head">
          <h2>{outcomes.length} 个结果</h2>
          <span>Predict：{wsStatus} · {spotSource} · 最后更新 {lastLiveText}</span>
        </div>
        <div className="primary-arb-list">
          {outcomes.map((item) => {
            const threshold = item.threshold;
            const makerPlan = calculateMakerNoPlan(item.targetFdv, settings, makerCalc.priceCents, makerCalc.shares);
            return (
              <article key={item.id} className={`primary-arb-row ${threshold?.status?.toLowerCase() || 'inactive'}`}>
                <div className="primary-arb-token">
                  {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <div className="primary-arb-mini-icon">CAT</div>}
                  <div>
                    <a href={item.url} target="_blank" rel="noreferrer">{item.label || item.title}</a>
                    <p>总成交量 {formatCompactUsd(item.volumeUsd)} {item.hourlyRate ? <><b>PP</b> {item.hourlyRate}/时</> : null}</p>
                    <p className="primary-arb-target-fdv">目标 FDV：{formatCompactUsd(item.targetFdv)}</p>
                    {threshold && (
                      <div className="primary-arb-thresholds">
                        <span>套利阈值：现货 ≤ {formatPrice(threshold.thresholdSpotPrice, 4)}</span>
                        <span>FDV ≤ {formatCompactUsd(threshold.thresholdFdv)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="primary-arb-prob">
                  <strong>{formatPercent(item.probability)}</strong>
                  <span>概率</span>
                </div>
                <div className="primary-arb-buy-buttons">
                  <button type="button" className="yes">是 {formatCents(item.yesPrice)}<small>买 YES</small></button>
                  <button type="button" className="no">否 {formatCents(item.noPrice)}<small>买 NO</small></button>
                </div>
                <div className="primary-arb-decision">
                    <span className={`primary-arb-status ${threshold?.status?.toLowerCase() || 'inactive'}`}>{statusText(threshold?.status)}</span>
                    <p className="primary-arb-brief">
                      {threshold
                        ? nextAction(item, threshold)
                        : nextAction(item, threshold)}
                    </p>
                    {threshold && (
                      <>
                        <div className="primary-arb-calc-grid">
                          <span>NO成本 <strong>{formatUsd(threshold.noCostForPlan, 1)}</strong></span>
                          <span>现货利润 <strong>{formatUsd(threshold.spotProfitAtTarget, 1)}</strong></span>
                          <span>差额 <strong className={threshold.edgeUsd >= 0 ? 'positive' : 'negative'}>{formatUsd(threshold.edgeUsd, 1)}</strong></span>
                          <span>50%兜底 <strong className={threshold.guardEdgeUsd >= 0 ? 'positive' : 'negative'}>{formatUsd(threshold.noProfitForPlan, 1)} / ≥{formatUsd(threshold.drawdownGuardUsd, 0)}</strong></span>
                          <span>触发价 <strong>NO≤{formatCents(threshold.triggerNoPrice)} / 现货≤{formatPrice(threshold.spotTriggerPrice, 4)}</strong></span>
                        </div>
                        <div className="primary-arb-compare-row">
                          <span>100U现货赚 <b>{formatUsd(threshold.spotProfitAtTarget, 1)}</b></span>
                          <span>100shares NO成本 <b>{formatUsd(threshold.noCostForPlan, 1)}</b></span>
                          <span>NO赢利 <b>{formatUsd(threshold.noProfitForPlan, 1)}</b></span>
                          <span>兜底需 ≥<b>{formatUsd(threshold.drawdownGuardUsd, 0)}</b></span>
                        </div>
                        {makerPlan && (
                          <div className={`primary-arb-maker-result ${makerPlan.isPositiveNow ? 'positive' : 'waiting'}`}>
                            <b>
                              {makerPlan.isPositiveNow
                                ? '散单可配现货'
                                : makerPlan.isCostCovered
                                  ? '兜底不足，不能这样配'
                                  : '目标价低于当前，暂不适合配现货'}
                            </b>
                            <div className="primary-arb-maker-grid">
                              <span>
                                <em>NO 成本 / 盈利</em>
                                <strong>{formatUsd(makerPlan.noCost, 2)} / {formatUsd(makerPlan.noProfit, 2)}</strong>
                              </span>
                              <span>
                                <em>NO回本等价现货</em>
                                <strong>{formatPrice(makerPlan.equivalentSpotPrice, 4)}</strong>
                              </span>
                              <span>
                                <em>当前价需配现货</em>
                                <strong>{Number.isFinite(makerPlan.requiredSpotBudget) ? formatUsd(makerPlan.requiredSpotBudget, 2) : '-'}</strong>
                                <small>涨到目标可赚 {formatUsd(makerPlan.requiredSpotProfit, 2)} · 回撤超 {formatPercent(makerPlan.requiredSpotMaxDrawdownPct, 1)} 亏损超过NO盈利</small>
                              </span>
                              <span>
                                <em>50%兜底最多可配</em>
                                <strong>{formatUsd(makerPlan.maxSpotBudgetByGuard, 2)}</strong>
                                <small>涨到目标可赚 {formatUsd(makerPlan.maxGuardSpotProfit, 2)} · {makerPlan.guardGap >= 0 ? `余量 ${formatUsd(makerPlan.guardGap, 2)}` : `超出 ${formatUsd(Math.abs(makerPlan.guardGap), 2)}`}</small>
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                </div>
              </article>
            );
          })}
          {!loading && !outcomes.length && <div className="primary-arb-empty">暂无结果，请确认市场链接或 API Key。</div>}
        </div>
      </section>
        </>
      )}
    </div>
  );
}
